import { VenetianMask } from 'lucide-react'

import { useDiscardIncognito, useIncognitoHeld } from '@/api/hooks'
import { Button, toast } from '@/components/ui'
import { formatBytes } from '@/lib/format'
import { useIncognito } from '@/lib/incognito'

import { SettingsSection } from './SettingsSection'
import { ToggleField } from './ToggleField'

/**
 * The private listening switch.
 *
 * The copy is deliberately literal about what does and does not happen. A file
 * *is* written to the server — streaming it requires that — and pretending
 * otherwise would be the one lie this feature must not tell. What it promises is
 * narrower: the track never joins your library, nothing is counted, and it is
 * deleted when you switch off.
 */
export function IncognitoSection() {
  const { active, set } = useIncognito()
  const held = useIncognitoHeld(active)
  const discard = useDiscardIncognito()

  const count = held.data?.count ?? 0
  const ttl = held.data?.ttl_hours ?? 6

  function change(next: boolean) {
    if (next) {
      set(true)
      return
    }
    // Switching off is what discards. Doing it before flipping the switch means
    // a failed request leaves the user still visibly in incognito rather than
    // out of it with files silently left behind.
    discard.mutate(undefined, {
      onSuccess: (result) => {
        set(false)
        toast.success(
          'Incognito off',
          result.discarded > 0
            ? `Discarded ${result.discarded} ${result.discarded === 1 ? 'song' : 'songs'}. Your library is unchanged.`
            : 'Nothing to discard.',
        )
      },
      onError: () =>
        toast.error(
          'Could not clear the session',
          'Still in incognito. Nothing was deleted — try again.',
        ),
    })
  }

  return (
    <SettingsSection
      icon={VenetianMask}
      title="Incognito"
    >
      <ToggleField
        label="Incognito listening"
        description="Listen without saving history."
        checked={active}
        onChange={change}
        disabled={discard.isPending}
      />

      {active ? (
        <div className="mt-4 rounded-card border border-border bg-surface-sunken p-3">
          <p className="text-sm text-text-muted">
            {count > 0
              ? `Holding ${count} ${count === 1 ? 'song' : 'songs'}${
                  held.data?.bytes ? ` · ${formatBytes(held.data.bytes)}` : ''
                }.`
              : 'Nothing held yet.'}{' '}
            Temporary audio is removed when you switch off or after {ttl} hours.
            Library and playlist songs stay.
          </p>

          {count > 0 ? (
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              loading={discard.isPending}
              onClick={() =>
                discard.mutate(undefined, {
                  onSuccess: (result) =>
                    toast.success(
                      'Session cleared',
                      `Discarded ${result.discarded} ${result.discarded === 1 ? 'song' : 'songs'}.`,
                    ),
                })
              }
            >
              Clear temporary audio
            </Button>
          ) : null}
        </div>
      ) : null}
    </SettingsSection>
  )
}
